import {Component, OnDestroy} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {BehaviorSubject, catchError, defer, Observable, of, shareReplay, Subject, takeUntil} from 'rxjs';
import {FloatBalance, WebFloatService} from 'web-angular-client';
import {AlertService} from '../../../sdk/service/alert.service';
import {handleLoadError} from '../../../sdk/util/load-error.util';
import {DEFAULT_UNAVAILABLE_TEXT} from './available-float-card/available-float-card.component';
import {TopUpFloatDialogComponent} from './top-up-float-dialog/top-up-float-dialog.component';

/**
 * Float Monitoring page. Renders the Available Float card (with its Top Up Float instructions) and the Float Engine
 * audit log; the warning banner and Refunds Issued card land once their endpoints exist.
 */
@Component({
    selector: 'app-float-monitoring',
    templateUrl: './float-monitoring.component.html',
    styleUrls: ['./float-monitoring.component.scss'],
    standalone: false
})
export class FloatMonitoringComponent implements OnDestroy
{
    readonly defaultUnavailableText = DEFAULT_UNAVAILABLE_TEXT;

    private readonly destroy$ = new Subject<void>();

    readonly balanceError$ = new BehaviorSubject<boolean>(false);

    readonly balanceUnavailableText$ = new BehaviorSubject<string | null>(null);

    readonly floatBalance$: Observable<FloatBalance | null> = defer(() =>
    {
        this.balanceError$.next(false);
        this.balanceUnavailableText$.next(null);
        return this.webFloatService.getFloatBalance();
    }).pipe(
        catchError((err) =>
        {
            if (err?.status === 422)
            {
                this.balanceError$.next(true);
                this.balanceUnavailableText$.next(err?.error?.errorMessage ?? null);
                return of(null);
            }
            return handleLoadError(err, this.balanceError$, 'Failed to load float balance.', null, this.alertService);
        }),
        takeUntil(this.destroy$),
        shareReplay(1)
    );

    constructor(
        private readonly webFloatService: WebFloatService,
        private readonly alertService: AlertService,
        private readonly modalService: NgbModal)
    {
    }

    openTopUpDialog(): void
    {
        this.modalService.open(TopUpFloatDialogComponent, {size: 'md', centered: true});
    }

    ngOnDestroy(): void
    {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
